import React, { ReactElement, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { getUserThunk, useSurveyRequests } from 'api/requests';

import dynamic from 'next/dynamic';
const Profile = dynamic(() => import('sections/Directory/DirectoryPage/components/Profile'));
const Filter = dynamic(() => import('sections/Directory/DirectoryPage/components/Filter'));
const Table = dynamic(() => import('sections/Directory/DirectoryPage/components/Table'));
const Modal = dynamic(() => import('ui/Modal'));
const DfaRequestAlert = dynamic(() => import('sections/Directory/DirectoryPage/components/DfaRequestAlert'));
const FinancialSurvey = dynamic(() => import('components/FinancialSurvey/FinancialSurvey'));
const Notification = dynamic(() => import('ui/Notification'));
const Button = dynamic(() => import('ui/Button'));
const FilterTags = dynamic(() => import('sections/Directory/DirectoryPage/components/FilterTags'));

import axios from 'axios';
import middleware from 'api/middleware';
import { css } from 'utils/helper';
import RootLayout from 'layouts/RootLayout';
import useWindowSize from 'utils/hooks/useWindowSize';
import { withSessionSsr } from 'lib/withSession';

export interface UserData {
  isAdmin: boolean;
  userAccountType: string;
}

const NotificationData = {
  imagePath: '/images/global/notification-image.svg',
  imageAlt: 'notification-image',
  title: 'Answer to a few questions',
  description:
    'Your feedback will help us learn what is important to you and allow us to better serve the transportation needs of your business.',
};

export default function DirectoryPage() {
  const { getSurveyConfigRequest } = useSurveyRequests();
  const [{ user }] = useCookies(['user']);
  const { width } = useWindowSize();

  const [currentUser, setCurrentUser] = useState<any>({});
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isRenderModal, setIsRenderModal] = useState<boolean>(false);
  const [isUnsubscribe, setIsUnsubscribe] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>('Are you sure you want to unsubscribe?');

  // State for "FinancialSurvey" component:
  const [surveyConfig, setSurveyConfig] = useState<any>({});
  const [isNotificationShown, setIsNotificationShown] = useState<boolean>(false);
  const [isSurveySuggested, setIsSurveySuggested] = useState<boolean>(false);
  const [confirmSurveyClose, setConfirmSurveyClose] = useState<boolean>(false);
  const [isSurveyOpened, setIsSurveyOpened] = useState<boolean>(false);
  const [isSurveyThanksOpened, setIsSurveyThanksOpened] = useState<boolean>(false);

  // State for "Events Survey Notification":
  const [isEventsSurveyShown, setIsEventsSurveyShown] = useState<boolean>(false);

  const [userTableData, setUserTableData] = useState<UserData>({ isAdmin: user?.isAdmin, userAccountType: '' });

  useEffect(() => {
    (async () => {
      const response = await getSurveyConfigRequest();
      response.status === 200 && setSurveyConfig(response.data.data);
      await middleware(
        () =>
          getUserThunk().then(({ status, data: _data }) => {
            const {
              data: { data },
            } = _data;
            status === 200 && setCurrentUser(data.self);
            data.actions?.some((item: any) => item.name === 'unsubscribe') && setIsRenderModal(true);
            data.actions?.forEach((item: any) => {
              if (item.name === 'survey') {
                // Hiding the "Financial Survey" for all users (do not remove "Financial Survey" itself).
                // To reveal the "Financial Survey" back,
                // set "setIsSurveySuggested" and "setIsNotificationShown" to "true"
                setIsSurveySuggested(false);
                setIsNotificationShown(false);
                setUserTableData({
                  isAdmin: data.self.is_admin,
                  userAccountType: data.self.company_id.account_type,
                });
              }
              if (item.name === 'survey-to-improve-experience') {
                setIsEventsSurveyShown(true);
                setUserTableData({
                  isAdmin: data.self.is_admin,
                  userAccountType: data.self.company_id.account_type,
                });
              }
              // else setIsNotificationShown(false);
            });
          }),
        {
          Function: 'getUserThunk()',
          Line: 40,
          Component: 'resources/js/Pages/Directory/Index.tsx',
          Author: '#Vlad',
          errorInfo: 'Error of unknown origin',
        }
      );
      setIsLoaded(true);
    })();
  }, []);

  useEffect(() => {
    if (!isUnsubscribe) setTimeout(() => setIsRenderModal(false), 300);
  }, [isUnsubscribe]);

  useEffect(() => {
    if (isRenderModal) setIsUnsubscribe(true);
  }, [isRenderModal]);

  const sendUnsubscribe = async (type: 'ok' | 'cancel') => {
    type === 'cancel' && setIsUnsubscribe(false);
    await axios
      .post(`/api/unsubscribe${type === 'cancel' ? '?cancel=true' : ''}`)
      .then(() => setTimeout(() => setIsUnsubscribe(false), 900))
      .catch(() => {
        setModalTitle('Server error. Contact with support');
        setTimeout(() => setIsUnsubscribe(false), 5 * 1000);
      });
  };

  if (width === 0) return null;

  return (
    <>
      <section className="directory">
        <div className="container">
          {isNotificationShown ? (
            <Notification
              setIsSurveyOpened={setIsSurveyOpened}
              imagePath={NotificationData.imagePath}
              imageAlt={NotificationData.imageAlt}
              title={NotificationData.title}
              description={NotificationData.description}
              linkText="Go to the survey"
              isLoading={isLoaded}
            />
          ) : (
            <></>
          )}

          {isEventsSurveyShown ? (
            <Notification
              // setIsSurveyOpened={setIsSurveyOpened}
              imagePath={NotificationData.imagePath}
              imageAlt={NotificationData.imageAlt}
              title="Help us to learn more about your company"
              description="We have developed this survey to improve your membership in DFA. Your feedback helps us to learn more about your company and decide which features to build and, what improvements should be made to our platform."
              linkText="Go to the survey"
              isLoading={isLoaded}
              linkPath={'/feedback/events-survey/survey-to-improve-experience'}
            />
          ) : (
            <></>
          )}

          <Profile currentUser={currentUser} setCurrentUser={setCurrentUser} />

          <Filter />

          <FilterTags />

          {!!user && user?.type === 'REQUEST' && <DfaRequestAlert />}

          <Table userData={userTableData} />
        </div>
      </section>

      {isRenderModal ? (
        <Modal
          title={modalTitle}
          size="small"
          modalHandler={() => setIsUnsubscribe(false)}
          open={isUnsubscribe}
          setModalVisible={setIsUnsubscribe}
        >
          <div className="btn__wrapper">
            <div className="modal__body-btn btn btn-dark size-xxs" onClick={() => sendUnsubscribe('ok')}>
              Ok
            </div>
            <div className="modal__body-btn btn btn-outline-light size-xxs" onClick={() => sendUnsubscribe('cancel')}>
              Cancel
            </div>
          </div>
        </Modal>
      ) : (
        <></>
      )}

      {isSurveySuggested ? (
        <Modal
          size="medium"
          title="Would you be willing to answer a few questions?"
          text="We'd greatly appreciate your feedback. Our short survey takes about 7 minutes."
          modalHandler={() => setIsSurveySuggested(false)}
          open={isSurveySuggested}
          setModalVisible={setIsSurveySuggested}
          noIcon
          noRedirection
        >
          <div className="modal__buttons">
            <Button
              name="skip-survey"
              action={() => {
                setIsSurveySuggested(false);
                css(document.body, { overflow: 'visible' });
              }}
              stylePreset="bordered"
              customStyle={{ width: '170px', marginRight: '15px' }}
            >
              Skip survey
            </Button>
            <Button
              name="start-survey"
              action={() => {
                setIsSurveySuggested(false);
                setIsSurveyOpened(true);
              }}
              stylePreset="dark"
              customStyle={{ width: '170px' }}
            >
              Start survey
            </Button>
          </div>
        </Modal>
      ) : (
        <></>
      )}

      {isSurveyOpened ? (
        <Modal
          size="medium"
          modalHandler={() => setIsSurveyOpened(false)}
          open={isSurveyOpened}
          setModalVisible={setIsSurveyOpened}
          noIcon={true}
          noRedirection={true}
          modalStyles={{ width: 'unset', height: 'unset', padding: '8px', display: 'block' }}
        >
          <FinancialSurvey
            surveyConfig={surveyConfig}
            setIsSurveyOpened={setIsSurveyOpened}
            setConfirmSurveyClose={setConfirmSurveyClose}
            setIsSurveyThanksOpened={setIsSurveyThanksOpened}
            setIsNotificationShown={setIsNotificationShown}
          />
        </Modal>
      ) : (
        <></>
      )}

      {confirmSurveyClose ? (
        <Modal
          size="medium"
          title="Want to close the survey?"
          text="You may complete the survey later"
          modalHandler={() => setConfirmSurveyClose(false)}
          open={confirmSurveyClose}
          setModalVisible={setConfirmSurveyClose}
          noIcon={true}
          noRedirection={true}
        >
          <div className="modal__buttons">
            <Button
              name="close-survey"
              action={() => {
                setConfirmSurveyClose(false);
                setIsSurveyOpened(false);
                css(document.body, { overflow: 'visible' });
              }}
              stylePreset="bordered"
              customStyle={{ width: '170px', marginRight: '15px' }}
            >
              Finish
            </Button>
            <Button
              name="back-to-survey"
              action={() => {
                setConfirmSurveyClose(false);
                setIsSurveyOpened(true);
              }}
              stylePreset="dark"
              customStyle={{ width: '170px' }}
            >
              Back to the survey
            </Button>
          </div>
        </Modal>
      ) : (
        <></>
      )}

      {isSurveyThanksOpened ? (
        <Modal
          size="small"
          title="Thank you!"
          text="Your survey responses have been recorded."
          btnText="Main page"
          modalHandler={() => setIsSurveyThanksOpened(false)}
          open={isSurveyThanksOpened}
          setModalVisible={setIsSurveyThanksOpened}
          noIcon={false}
          noRedirection
        />
      ) : (
        <></>
      )}
    </>
  );
}

export const getServerSideProps = withSessionSsr(async function ({ req, res }) {
  const { user } = req.session;

  if (!user || !user.profile) {
    return {
      redirect: {
        destination: '/auth/sign-in',
        permanent: true,
      },
    };
  }

  if (!user?.profile && (user?.is_admin || !user?.hash)) {
    return {
      redirect: {
        destination: '/directory/virtual-office',
        statusCode: 301,
      },
    };
  }

  return {
    props: {
      title: 'Directory',
      path: '/directory',
    },
  };
});

DirectoryPage.getLayout = function getLayout(page: ReactElement) {
  return <RootLayout>{page}</RootLayout>;
};
